// src/components/cost-simulator/OnPremTcoPanel.tsx
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Server, Cpu, Zap, Building, Users, Wrench, DollarSign } from 'lucide-react';
import { formatCurrency } from '@/lib/costCalculations';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from '@/lib/charts/leaf';
import type { CostSimulatorConfig, MonthlyRollupEnhanced } from '@/lib/costCalculations';

interface OnPremTcoPanelProps {
  config: CostSimulatorConfig;
  onPremBreakdown: {
    capexTotal: number;
    monthlyCapexAmortized: number;
    monthlyPowerCost: number;
    monthlyAdminCost: number;
    monthlyFacilitiesCost: number;
    monthlyPersonnelCost: number;
    monthlyOpEx: number;
    monthlyTco: number;
  };
  serverCount: number;
  gpuCount: number;
  monthlyRollups: MonthlyRollupEnhanced[];
}

export const OnPremTcoPanel = ({
  config,
  onPremBreakdown,
  serverCount,
  gpuCount,
  monthlyRollups,
}: OnPremTcoPanelProps) => {
  const {
    capexTotal,
    monthlyPowerCost,
    monthlyAdminCost,
    monthlyFacilitiesCost,
    monthlyPersonnelCost,
    monthlyOpEx,
  } = onPremBreakdown;

  const year1Rollups = monthlyRollups.slice(0, 12);
  const fiveYearOpEx = monthlyOpEx * 60;
  const fiveYearTco = capexTotal + fiveYearOpEx;

  // Pie chart data for OpEx breakdown - colors match icons
  const opexPieData = [
    { name: 'Power', value: monthlyPowerCost, color: '#eab308' },      // yellow-500
    { name: 'Admin', value: monthlyAdminCost, color: '#3b82f6' },      // blue-500
    { name: 'Facilities', value: monthlyFacilitiesCost, color: '#a855f7' }, // purple-500
    { name: 'Personnel', value: monthlyPersonnelCost, color: '#22c55e' },   // green-500
  ].filter(d => d.value > 0);

  // TCO breakdown pie
  const tcoPieData = [
    { name: 'CapEx', value: capexTotal, color: 'hsl(var(--primary))' },
    { name: 'OpEx (5Y)', value: fiveYearOpEx, color: 'hsl(var(--destructive))' },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Server className="h-4 w-4" />
          On-Premises TCO Breakdown
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Infrastructure Summary */}
        <div className="bg-primary/5 rounded-lg p-4">
          <h4 className="font-medium mb-3">Infrastructure Summary</h4>
          <div className="grid grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold">{serverCount}</div>
              <div className="text-xs text-muted-foreground">Servers</div>
            </div>
            <div>
              <div className="text-2xl font-bold">{gpuCount}</div>
              <div className="text-xs text-muted-foreground">GPUs</div>
            </div>
            <div>
              <div className="text-2xl font-bold">{config.gpusPerServer}</div>
              <div className="text-xs text-muted-foreground">GPUs/Server</div>
            </div>
            <div>
              <div className="text-2xl font-bold">{(config.powerKwPerServer * serverCount).toFixed(1)}</div>
              <div className="text-xs text-muted-foreground">Total kW</div>
            </div>
          </div>
        </div>

        {/* CapEx Breakdown */}
        <div className="border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <DollarSign className="h-4 w-4 text-primary" />
            <h4 className="font-medium">Capital Expenditure (CapEx)</h4>
            <Badge variant="outline">Upfront</Badge>
          </div>
          <div className="bg-muted/30 rounded-lg p-3">
            <div className="font-mono text-sm space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Server Cost:</span>
                <span>{formatCurrency(config.capexPerServer)} × {serverCount} servers</span>
              </div>
              <div className="border-t pt-2 flex justify-between font-bold">
                <span>Total CapEx:</span>
                <span className="text-lg text-primary">{formatCurrency(capexTotal)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* OpEx Breakdown */}
        <div className="border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Wrench className="h-4 w-4 text-destructive" />
            <h4 className="font-medium">Operating Expenditure (OpEx)</h4>
            <Badge variant="outline">Monthly</Badge>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* OpEx Details */}
            <div className="space-y-3">
              <div className="flex items-center justify-between p-2 bg-muted/30 rounded">
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-yellow-500" />
                  <span className="text-sm">Power</span>
                </div>
                <span className="font-mono">{formatCurrency(monthlyPowerCost)}</span>
              </div>
              <div className="text-xs text-muted-foreground ml-6">
                {config.powerKwPerServer} kW × {serverCount} servers × 720 hrs × ${config.electricityPerKwh}/kWh
              </div>

              <div className="flex items-center justify-between p-2 bg-muted/30 rounded">
                <div className="flex items-center gap-2">
                  <Wrench className="h-4 w-4 text-blue-500" />
                  <span className="text-sm">Admin/Support</span>
                </div>
                <span className="font-mono">{formatCurrency(monthlyAdminCost)}</span>
              </div>
              <div className="text-xs text-muted-foreground ml-6">
                ${config.adminPerServerMonthly} × {serverCount} servers
              </div>

              <div className="flex items-center justify-between p-2 bg-muted/30 rounded">
                <div className="flex items-center gap-2">
                  <Building className="h-4 w-4 text-purple-500" />
                  <span className="text-sm">Facilities</span>
                </div>
                <span className="font-mono">{formatCurrency(monthlyFacilitiesCost)}</span>
              </div>

              <div className="flex items-center justify-between p-2 bg-muted/30 rounded">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-green-500" />
                  <span className="text-sm">Personnel</span>
                </div>
                <span className="font-mono">{formatCurrency(monthlyPersonnelCost)}</span>
              </div>

              <div className="border-t pt-2 flex justify-between font-bold">
                <span>Monthly OpEx:</span>
                <span className="text-destructive">{formatCurrency(monthlyOpEx)}</span>
              </div>
            </div>

            {/* OpEx Pie Chart */}
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={opexPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {opexPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: number) => formatCurrency(value)}
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      borderColor: 'hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* 5-Year TCO Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border rounded-lg p-4">
            <h4 className="font-medium mb-3">5-Year TCO Summary</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">CapEx (Upfront)</span>
                <span className="font-mono">{formatCurrency(capexTotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">OpEx (60 months)</span>
                <span className="font-mono">{formatCurrency(fiveYearOpEx)}</span>
              </div>
              <div className="border-t pt-2 flex justify-between font-bold text-lg">
                <span>Total TCO</span>
                <span>{formatCurrency(fiveYearTco)}</span>
              </div>
            </div>
          </div>

          {/* TCO Pie Chart */}
          <div className="h-[150px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={tcoPieData}
                  cx="50%"
                  cy="50%"
                  outerRadius={50}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {tcoPieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number) => formatCurrency(value)}
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    borderColor: 'hsl(var(--border))',
                    borderRadius: '8px',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Monthly TCO Table */}
        <div className="border rounded-lg p-4">
          <h4 className="font-medium mb-3">Cumulative TCO (First 12 Months)</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 pr-4">Month</th>
                  <th className="text-right py-2 px-2">CapEx</th>
                  <th className="text-right py-2 px-2">OpEx</th>
                  <th className="text-right py-2 pl-2">Cumulative</th>
                </tr>
              </thead>
              <tbody>
                {year1Rollups.map((rollup) => (
                  <tr key={rollup.monthKey} className="border-b border-border/50">
                    <td className="py-2 pr-4">{rollup.monthKey}</td>
                    <td className="text-right py-2 px-2 font-mono text-xs">
                      {formatCurrency(rollup.upfrontCapex)}
                    </td>
                    <td className="text-right py-2 px-2 font-mono text-xs">
                      {formatCurrency(rollup.monthlyOpEx)}
                    </td>
                    <td className="text-right py-2 pl-2 font-medium">
                      {formatCurrency(rollup.cumOnPremTcoUSD)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
